import EditIcon from '@mui/icons-material/Edit'
import { Button, Chip, Stack, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'
import * as endPoints from '@/endpoints'
import { createdViaLabel } from '@/features/dashboard/site-admin/orgCreatedVia'
import { FEATURE_FLAGS_BY_KEY } from '@/features/dashboard/site-admin/orgFeatureFlags'
import { flavourLabel } from '@/features/dashboard/site-admin/orgFlavours'
import axios from '@/lib/axios'
import EntityLink from '../EntityLink'
import PeekLayout, {
    PeekDetails,
    PeekPlaceholder,
    PeekSection
} from './PeekLayout'

const fuels = (org) =>
    [org.has_electricity && 'Electricity', org.has_gas && 'Gas']
        .filter(Boolean)
        .join(', ') || 'None yet'

/**
 * A customer organisation, for site admins: what kind of org it is, how big,
 * how it's set up, and which features it has on. Uses the same query as the
 * Edit Organisation page, so opening one after the other is instant.
 */
export default function OrganisationPeek({ id }) {
    const query = useQuery({
        queryKey: ['org', id],
        queryFn: () =>
            axios
                .get(`${endPoints.ORG}${id}/`)
                .then((response) => response.data),
        retry: false
    })
    const org = query.data

    if (!org) {
        return <PeekPlaceholder kind="Organisation" loading={query.isLoading} />
    }

    const enabled = Object.entries(org.effective_features ?? {})
        .filter(([key, on]) => on && !FEATURE_FLAGS_BY_KEY[key]?.alwaysOn)
        .map(([key]) => FEATURE_FLAGS_BY_KEY[key]?.label ?? key)
        .sort()

    return (
        <PeekLayout
            kind="Organisation"
            title={org.name}
            subtitle={[org.short_name, flavourLabel(org.flavour)]
                .filter(Boolean)
                .join(' · ')}
            actions={
                <Button
                    component={RouterLink}
                    to={`/site-admin/orgs/${org.id}/edit`}
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon fontSize="small" />}
                >
                    Edit organisation
                </Button>
            }
        >
            <PeekSection title="Details">
                <PeekDetails
                    rows={[
                        { label: 'Users', value: org.user_count ?? '—' },
                        {
                            label: 'Partner',
                            value: org.partner ? (
                                <EntityLink type="partner" id={org.partner}>
                                    {org.partner_name || 'Partner'}
                                </EntityLink>
                            ) : (
                                'None (managed directly)'
                            )
                        },
                        { label: 'Fuels', value: fuels(org) },
                        {
                            label: 'People directory',
                            value: org.has_mis ? 'Connected' : 'Not connected'
                        },
                        {
                            label: 'Billing',
                            value: (
                                <Chip
                                    size="small"
                                    color={
                                        org.is_billing_active
                                            ? 'success'
                                            : 'default'
                                    }
                                    label={
                                        org.is_billing_active
                                            ? 'Active'
                                            : 'Inactive'
                                    }
                                />
                            )
                        },
                        {
                            label: 'Set up',
                            value: org.setup_completed_at
                                ? DateTime.fromISO(
                                      org.setup_completed_at
                                  ).toLocaleString(DateTime.DATE_MED)
                                : 'Not finished'
                        },
                        {
                            label: 'Created via',
                            value:
                                [
                                    createdViaLabel(org.created_via),
                                    org.created_by?.name ||
                                        org.created_by?.email
                                ]
                                    .filter(Boolean)
                                    .join(' · ') || '—'
                        }
                    ]}
                />
            </PeekSection>

            <PeekSection title={`Features switched on (${enabled.length})`}>
                {enabled.length > 0 ? (
                    <Stack
                        direction="row"
                        spacing={0.75}
                        flexWrap="wrap"
                        useFlexGap
                    >
                        {enabled.map((label) => (
                            <Chip
                                key={label}
                                size="small"
                                label={label}
                                variant="outlined"
                            />
                        ))}
                    </Stack>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Only the always-on features.
                    </Typography>
                )}
            </PeekSection>
        </PeekLayout>
    )
}

OrganisationPeek.propTypes = {
    id: PropTypes.string.isRequired
}
